import { Box, Paper, TableContainer, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Chip from '../../components/Chip/Chip';
import DefaultTable from '../../components/DefaultTable/DefaultTable';
import TableToolbar from '../../components/DefaultTable/TableToolbar/TableToolbar';
import GlobalSearch from '../../components/GlobalSearch/GlobalSearch';
import PageTitle from '../../components/PageTitle/PageTitle';
import { TalentStatus } from '../../enums/talentStatus';
import { TalentType } from '../../enums/talentType';
import { useShowFilterOrSearch } from '../../hooks/useShowFilterOrSearch';
import { useTableFunctions } from '../../hooks/useTableFunctions';
import { HeadCell } from '../../models/headCell';
import { Pagination } from '../../models/pagination';
import { Talent } from '../../models/talent';
import { TALENT } from '../../router/constants';
import { talentService } from '../../services/talentService';
import {
  createDownloadFile,
  removeEmptyObjectFields
} from '../../utils/helpers';
import { getRoundStatusStyle } from '../../utils/statusRoundStyle';
import FilterTalents, { FilterTalentsForm } from './FilterTalents';

const initialTalentsColumnsData: HeadCell[] = [
  {
    id: 'id',
    label: 'Id',
    sortable: true,
    visible: true,
    render: (talent: Talent) => talent.id
  },
  {
    id: 'firstName',
    label: 'Talent',
    sortable: true,
    visible: true,
    render: (talent: Talent) => talent.firstName + ' ' + talent.lastName
  },
  {
    id: 'company.name',
    label: 'Company',
    sortable: true,
    visible: true,
    render: (talent: Talent) => talent.company.name
  },
  {
    id: 'paymentType',
    label: 'Type',
    sortable: true,
    visible: true,
    render: (talent: Talent) => TalentType[talent.accountType]
  },
  {
    id: 'city',
    label: 'City',
    sortable: true,
    visible: true,
    render: (talent: Talent) => talent.city
  },
  {
    id: 'state',
    label: 'State',
    sortable: true,
    visible: true,
    render: (talent: Talent) => talent.state
  },
  {
    id: 'status',
    label: 'Status',
    sortable: true,
    visible: true,
    thStyle: { width: 260, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (talent: Talent) => (
      <Box sx={getRoundStatusStyle(TalentStatus[talent.status])}>
        {TalentStatus[talent.status]}
      </Box>
    )
  }
];

const TalentsList = (): JSX.Element => {
  const initialFilterData: FilterTalentsForm = {
    talents: [],
    accountType: '',
    city: '',
    state: '',
    status: []
  };
  const [paginationTalents, setPaginationTalents] = useState<
    Pagination<Talent> | undefined
  >(undefined);
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [showFilters, showSearch, onFilterClick, onSearchClick] =
    useShowFilterOrSearch();
  const {
    columnsData,
    page,
    rowsPerPage,
    sortBy,
    sortType,
    searchTerm,
    filterData,
    onRequestSort,
    onChangePage,
    onChangeRowsPerPage,
    onChangeColumnVisibility,
    onSearchChange,
    onFilterChange
  } = useTableFunctions<FilterTalentsForm>(
    initialTalentsColumnsData,
    initialFilterData,
    'firstName',
    'asc'
  );

  useEffect(() => {
    getTalents();
  }, [page, rowsPerPage, sortBy, sortType, filterData, searchTerm]);

  const getTalents = () => {
    talentService
      .getTalents(generateFilterBody(), {
        params: {
          page: page,
          size: rowsPerPage,
          sort: `${sortBy},${sortType}`
        }
      })
      .then((response) => {
        setPaginationTalents(response.data);
        setLoading(false);
      });
  };

  const handleExport = () => {
    talentService.exportTalents(generateFilterBody()).then((response) => {
      createDownloadFile(response.data, 'talents');
    });
  };

  const generateFilterBody = () => {
    let body = removeEmptyObjectFields(filterData);
    if (body.talents) {
      body = {
        ...body,
        talents: filterData.talents
          ? filterData.talents.map((talent) => talent.id)
          : []
      };
    }
    if (searchTerm) {
      body = {
        ...body,
        searchTerm: searchTerm
      };
    }
    return body;
  };

  return (
    <>
      <Box display="flex" alignItems="center">
        <PageTitle>View All Talent</PageTitle>
        <Chip size={paginationTalents?.totalElements} />
      </Box>
      <TableContainer component={Paper} sx={{ minHeight: '320px' }}>
        <TableToolbar
          viewTableColumnsProps={{
            columns: columnsData,
            onColumnClick: onChangeColumnVisibility
          }}
          onFilter={onFilterClick}
          onSearch={onSearchClick}
          onExport={handleExport}
        >
          {showSearch && (
            <GlobalSearch
              onSearchChange={onSearchChange}
              searchTerm={searchTerm}
            />
          )}
          {showFilters && (
            <FilterTalents
              handleFormSubmit={onFilterChange}
              filterData={filterData}
            />
          )}
        </TableToolbar>
        <DefaultTable
          columns={columnsData}
          data={paginationTalents?.content}
          rowClick={(talent: Talent) => navigate(`${TALENT}/${talent.id}`)}
          sortProps={{ sortBy, sortType, onRequestSort }}
          paginationProps={{
            page,
            rowsPerPage,
            onChangePage,
            onChangeRowsPerPage,
            totalElements: paginationTalents?.totalElements
          }}
        />
        {!loading && !paginationTalents?.content.length && (
          <Typography variant="h5" align="center" m={4}>
            No talents found!
          </Typography>
        )}
      </TableContainer>
    </>
  );
};

export default TalentsList;
